import React, { useEffect, useRef, useState } from "react"

import { useManagedContext } from "../../components/hooks"
import { SectionHeading, TableCustom } from "../../components/common"
import { ALERT_TYPE, ESTADOS_FIRMA, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis"
import { getDateSerialize, getDateToString } from "../../utils/convert"
import FirmaDigitalFilters from "./components/FirmaDigitalFilters"
import { standardRequest } from "../../utils/requests"
import { firmarArchivos, getDownloadCell, useGetListaCell } from "./utils"
import FirmaDigitalObservacionModal from "./components/FirmaDigitalObservacionModal"
import { GetPayloadJWT } from "../../utils/helpers"
import { useUsuarios } from "../../components/hooks/useUsuarios"

const USUARIO_CACHE_KEY = 'Administracion_UsuarioCache'

const FirmaDigitalView = () => {
    const [showData, setShowData] = useState(false)
    const [firmas, setFirmas] = useState([])
    const [filters, setFilters] = useState({
        idUsuarioSolicitud: 0,
        idEstadoFirma: ESTADOS_FIRMA.PENDIENTE,
        idTipoFirma: 0,
        fechaSolicitudDesde: null,
        fechaSolicitudHasta: null,
        fechaFirmaDesde: null,
        fechaFirmaHasta: null
    })
    const [pendingChange, setPendingChange] = useState(false)
    const [permisoNombres, setPermisoNombres] = useState({})
    const [tableVisible, setTableVisible] = useState(false)
    const [action, setAction] = useState(null)

    const { setIsLoading } = useManagedContext()

    const tableRef = useRef(null)

    const getListaCell = useGetListaCell()

    const [getNombreUsuario, resolveUsuarios, resolveAllUsuarios] = useUsuarios({ key: USUARIO_CACHE_KEY })

    const [usuarioToken, setUsuarioToken] = useState(null);

    useEffect(async () => {
        setIsLoading(true)
        standardRequest(REQUEST_METHOD.GET, APIS.URLS.PERMISO)
        .then(data => {
            const result = {}
            data.forEach(permiso => result[permiso.id] = permiso.nombre.replace(/^firma digital[\s-]*/i, ''))
            setPermisoNombres(result)
            search(filters)
        })
        .catch(error => {}) //excepcion administrada en standardRequest
        .finally(() => setIsLoading(false));
        //busco los datos del usuario del token
        getUsuarioToken();
    }, [])

    //#region Functions
    const buildFirmaDigitalUrl = (currentFilters) =>
        `${APIS.URLS.FIRMA_DIGITAL}?` +
        `idUsuarioSolicitud=${encodeURIComponent(currentFilters.idUsuarioSolicitud || '')}&` +
        `idEstadoFirma=${encodeURIComponent(currentFilters.idEstadoFirma || '')}&` +
        `idTipoFirma=${encodeURIComponent(currentFilters.idTipoFirma || '')}&` +
        `fechaSolicitudDesde=${encodeURIComponent(getDateSerialize(currentFilters.fechaSolicitudDesde))}&` +
        `fechaSolicitudHasta=${encodeURIComponent(getDateSerialize(currentFilters.fechaSolicitudHasta))}&` +
        `fechaFirmaDesde=${encodeURIComponent(getDateSerialize(currentFilters.fechaFirmaDesde))}&` +
        `fechaFirmaHasta=${encodeURIComponent(getDateSerialize(currentFilters.fechaFirmaHasta))}`

    const onSelected = (selected) => {
        let newPendingChange = false
        const newFirmas = [...firmas]
        for (let i = 0; i < selected.length; i++) {
            if (selected[i].selected)
                newPendingChange = true

            for (let j = 0; j < firmas.length; j++) {
                if (newFirmas[j].id === selected[i].id) {
                    newFirmas[j].selected = selected[i].selected
                    break
                }
            }
        }
        setFirmas(newFirmas)
        setPendingChange(newPendingChange)
    }

    const search = (currentFilters = filters, callback) => {
        setFilters(currentFilters)
        setIsLoading(true)
        standardRequest(REQUEST_METHOD.GET, buildFirmaDigitalUrl(currentFilters))
        .then(data => {
            const relevantUserIds = []
            const newData = data.map(item => {
                relevantUserIds.push(item.idUsuarioSolicitud)
                if (item.idUsuarioFirma) relevantUserIds.push(item.idUsuarioFirma)
                if (item.fechaSolicitud) item.fechaSolicitud = new Date(item.fechaSolicitud)
                if (item.fechaFirma) item.fechaFirma = new Date(item.fechaFirma)

                return { ...item, selectable: item.idEstadoFirma === ESTADOS_FIRMA.PENDIENTE }
            }).sort((a, b) => b.id - a.id)

            resolveUsuarios(relevantUserIds)
            setFirmas(newData)
            setShowData(true)
            setTableVisible(true)
            setPendingChange(false)

            if (callback) callback();
        })
        .catch(error => {}) //excepcion administrada en standardRequest
        .finally(() => setIsLoading(false))

        if (tableRef && tableRef.current) tableRef.current.manualReset()
    }

    const getUsuarioToken = () => {
        setIsLoading(true)
        standardRequest(REQUEST_METHOD.GET, `${APIS.URLS.SEGURIDAD_USUARIO}/token`)
        .then(data => {
            setUsuarioToken(data)
        })
        .catch(error => {}) //excepcion administrada en standardRequest
        .finally(() => setIsLoading(false))
    }

    const firmar = async (observacion) => {

        if (!usuarioToken) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se pudo encontrar los datos del usuario/a')
            return
        }

        setIsLoading(true)
        setAction(null)

        try {
            const imagenFirmaDigital = usuarioToken.imagenFirmaDigital
            const identificadorFirmaDigital = usuarioToken.identificadorFirmaDigital
            if (!identificadorFirmaDigital) {
                ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'No se pudo encontrar el identificador de firma digital del usuario/a')
                setIsLoading(false)
                return
            }

            await firmarArchivos(identificadorFirmaDigital, imagenFirmaDigital, firmas.filter(x => x.selected), observacion)
            ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Los archivos fueron firmados')
            search()
        }
        catch (e) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Algunos archivos no pudieron ser firmados')
            setIsLoading(false)
        }
    }

    const rechazar = async (observacion) => {
        setIsLoading(true)
        setAction(null)

        try {
            const selected = firmas.filter(x => x.selected)
            await Promise.all(selected.map(({ id }) => standardRequest(
                REQUEST_METHOD.PUT,
                `${APIS.URLS.FIRMA_DIGITAL}/rechaza/${id}`,
                { observacion },
            )))
            ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Los documentos fueron rechazados')
            search()
        }
        catch (e) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error al rechazar documentos')
            setIsLoading(false)
        }
    }

    const download = async (item) => {
        try {
            const { file64 } = await standardRequest(REQUEST_METHOD.GET, `${APIS.URLS.FIRMA_DIGITAL}/${item.id}/signed-file`)

            const a = document.createElement("a")
            a.href = "data:application/pdf;base64," + file64
            a.download = "documento.pdf"
            a.click()
        }
        catch { /*excepcion administrada en standardRequest*/ }
    }
    //#endregion

    const mostrarFirma = filters.idEstadoFirma !== ESTADOS_FIRMA.PENDIENTE
    const columnasFirma = mostrarFirma ? [
        { id:'fechaFirma', Header: 'Fecha firma', accessor: 'fechaFirma', Cell: (data) => getDateToString(data.value, false), width:'9%' },
        { id:'idUsuarioFirma', Header: 'Firmante', accessor: 'idUsuarioFirma', Cell: ({ value }) => getNombreUsuario(value), width:'9%' }
    ] : []

    return <>

        <SectionHeading titles={['Firmas digitales']} />

        {!!action && (
            <FirmaDigitalObservacionModal
                action={action}
                onConfirm={(observacion) => (action === 'firma' ? firmar : rechazar)(observacion)}
                onCancel={() => setAction(null)}
            />
        )}

        <section className='section-accordion'>
            <FirmaDigitalFilters
                onOpenUsuario={resolveAllUsuarios}
                onSearch={(selectedFilters) => search(selectedFilters)}
            />

            {tableVisible && (
                <TableCustom
                    className={'TableCustomBase m-top-20'}
                    useSelectedField
                    selectionAllowed = {(row) => row.idEstadoFirma === ESTADOS_FIRMA.PENDIENTE}
                    ref={tableRef}
                    onItemsSelected={onSelected}
                    data={showData ? firmas : []}
                    columns={[
                        { id:'fechaSolicitud', Header: 'Solicitud', accessor: 'fechaSolicitud', Cell: (data) => getDateToString(data.value, false), width:'8%' },
                        { id:'idUsuarioSolicitud', Header: 'Solicitante', accessor: 'idUsuarioSolicitud', Cell: ({ value }) => getNombreUsuario(value), width: mostrarFirma ? '10%' : '12%' },
                        { id:'idPermiso', Header: 'Rol', accessor: 'idPermiso', Cell: ({ value }) => permisoNombres[value] ?? '', width: mostrarFirma ? '10%' : '15%' },
                        { id:'idEstadoFirma', Header: 'Estado', accessor: 'idEstadoFirma', Cell: getListaCell('EstadoFirma'), width:'8%' },
                        { id:'idTipoFirma', Header: 'Tipo de documento', accessor: 'idTipoFirma', Cell: getListaCell('TipoFirma'), width: mostrarFirma ? '18%' : '20%' },
                        { id:'detalle', Header: 'Detalle', accessor: 'detalle', width: mostrarFirma ? '10%' : '14%' },
                        { id:'observacion', Header: 'Observación', accessor: 'observacion', width: mostrarFirma ? '9%' : '14%' },
                        ...columnasFirma,
                        { id:'descarga', Header: '', accessor: 'id', Cell: getDownloadCell(download), width:'4%', ellipsis: false, disableSortBy: true }
                    ]}
                />
            )}
        </section>

        <footer className='footer footer-action'>
            <div className='footer-action-container'>
                <button 
                    className="btn action-button float-start" 
                    onClick={() => setAction('rechaza')} 
                    disabled={!pendingChange}
                >
                        Rechazar
                </button>
                <button
                    className="btn action-button-fill float-end"
                    onClick={() => setAction('firma')}
                    disabled={!pendingChange}
                >
                    Firmar
                </button>
            </div> 
        </footer>
    </>
}

export default FirmaDigitalView
